import { Button, Stack } from '@mantine/core';
import { Form, Formik } from 'formik';
import { find } from 'lodash/fp';
import React, { FC } from 'react';

import { useDeviceModels } from '@portals/api/organizations';
import { StandardField } from '@portals/autoformik';
import { ModalBody, ModalFooter } from '@portals/core';
import { AuthMethodType, FieldTypeEnum } from '@portals/types';

import { DeviceNameField } from './DeviceNameField';
import { ModelField } from './ModelField';
import { FormComponentCommonProps } from './types';
import { VendorField } from './VendorField';

interface MacAndSnFormFields {
  cloud_id?: string;
  mac?: string;
  sn?: string;
  name: string;
  model: string;
  license_id: string;
}

const INITIAL_VALUES: MacAndSnFormFields = {
  cloud_id: '',
  mac: '',
  sn: '',
  name: '',
  model: '',
  license_id: '',
};

const ByModelForm: FC<FormComponentCommonProps> = ({
  selectedPartnerId,
  setSelectedPartnerId,
  onSubmit,
  handleClose,
  isSubmitDisabled,
}) => {
  const deviceModels = useDeviceModels();

  return (
    <Formik initialValues={INITIAL_VALUES} onSubmit={onSubmit}>
      {(formikProps) => {
        const { values, handleBlur, handleChange, setFieldValue } = formikProps;
        const model = values.model
          ? find({ id: values.model }, deviceModels?.data)
          : null;
        const authMethod = model ? model.auth_method : null;

        return (
          <Form>
            <ModalBody>
              <Stack>
                <VendorField
                  vendor={selectedPartnerId}
                  setVendor={setSelectedPartnerId}
                />
                <ModelField
                  partnerId={selectedPartnerId}
                  fieldName="model"
                  value={values.model}
                  setFieldValue={setFieldValue}
                />

                {authMethod === AuthMethodType.CloudId && (
                  <CloudIdField {...formikProps} />
                )}

                {authMethod === AuthMethodType.MacSN && (
                  <MacSNFields {...formikProps} />
                )}

                <DeviceNameField
                  value={values.name}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                />
              </Stack>
            </ModalBody>
            <ModalFooter position="right">
              <Button type="button" variant="default" onClick={handleClose}>
                Cancel
              </Button>

              <Button
                type="submit"
                data-testid="add-device-to-space-claim-device-button"
                disabled={isSubmitDisabled || !values.model}
              >
                Claim device
              </Button>
            </ModalFooter>
          </Form>
        );
      }}
    </Formik>
  );
};

const MacSNFields = ({ values, handleBlur, handleChange, setFieldValue }) => (
  <>
    <StandardField
      field={{
        type: FieldTypeEnum.Text,
        name: 'mac',
        title: 'MAC Address',
        required: true,
      }}
      disabled={false}
      readOnly={false}
      value={values.mac}
      handleChange={handleChange}
      setFieldValue={setFieldValue}
      handleBlur={handleBlur}
      className="mb-3"
    />

    <StandardField
      field={{
        type: FieldTypeEnum.Text,
        name: 'sn',
        title: 'Serial number',
        required: true,
      }}
      disabled={false}
      readOnly={false}
      value={values.sn}
      handleChange={handleChange}
      setFieldValue={setFieldValue}
      handleBlur={handleBlur}
      className="mb-3"
    />
  </>
);

const CloudIdField = ({ values, handleBlur, handleChange, setFieldValue }) => (
  <StandardField
    field={{
      type: FieldTypeEnum.Text,
      name: 'cloud_id',
      title: 'Device Cloud ID',
      required: true,
    }}
    disabled={false}
    readOnly={false}
    value={values.cloud_id}
    handleChange={handleChange}
    setFieldValue={setFieldValue}
    handleBlur={handleBlur}
    className="mb-3"
  />
);

export default ByModelForm;
